import React, { useMemo } from 'react';
import { groupBy, sumBy, maxBy } from 'lodash';
import moment from 'moment-timezone';

import { IconButton, Table, TableHead, TableRow, TableCell, TableBody } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';

import { ApiKeyInfo, ApiKeyStatsDay } from 'common/types';
import { useTranslation } from 'services/localization';

interface Props {
	data: ApiKeyInfo[];
	stats: ApiKeyStatsDay[];
	onDelete: (keyId: string) => void;
}

const ApiKeysTable = (props: Props) => {
	const { t } = useTranslation();
	const statsByKey = useMemo(() => {
		return groupBy(props.stats, 'keyId');
	}, [props.stats]);

	const last7DaysByKey = useMemo(() => {
		const now = moment();
		return Object.keys(statsByKey).reduce((result, keyId) => {
			const statsObjects = statsByKey[keyId];
			const filtered = statsObjects.filter(
				(statsObject) => now.diff(moment(statsObject.date), 'days') <= 7,
			);
			result[keyId] = sumBy(filtered, (i) => i.calls);
			return result;
		}, {} as { [keyId: string]: number });
	}, [statsByKey]);

	const latestUsagePeriod = useMemo(() => {
		return Object.keys(statsByKey).reduce((result, keyId) => {
			const statsObjects = statsByKey[keyId];
			result[keyId] = maxBy(statsObjects, (i) => moment(i.date).valueOf());
			return result;
		}, {} as { [keyId: string]: ApiKeyStatsDay | undefined });
	}, [statsByKey]);

	const renderLast7Days = (keyId: string) => {
		return last7DaysByKey[keyId] ?? 0;
	};

	const renderLastUpdated = (keyId: string) => {
		const stats = latestUsagePeriod[keyId];

		if (!stats) return 'N/A';
		return moment.unix(stats.updated.seconds).format('MMMM Do YYYY');
	};

	return (
		<Table>
			<TableHead>
				<TableRow>
					<TableCell>{t('Integrations.ApiKeysTable.label', 'Label')}</TableCell>
					<TableCell>{t('Integrations.ApiKeysTable.id', 'ID')}</TableCell>
					<TableCell>{t('Integrations.ApiKeysTable.created', 'Created')}</TableCell>
					<TableCell>
						{t('Integrations.ApiKeysTable.callsLast7Days', 'Calls (last 7 days)')}
					</TableCell>
					<TableCell>{t('Integrations.ApiKeysTable.lastUsed', 'Last used')}</TableCell>
					<TableCell></TableCell>
				</TableRow>
			</TableHead>
			<TableBody>
				{props.data.map((key) => (
					<TableRow key={key.keyId}>
						<TableCell>{key.label}</TableCell>
						<TableCell>{key.keyId}</TableCell>
						<TableCell>{moment.unix(key.created?.seconds).format('MMMM Do YYYY')}</TableCell>
						<TableCell>{renderLast7Days(key.keyId)}</TableCell>
						<TableCell>{renderLastUpdated(key.keyId)}</TableCell>
						<TableCell>
							<IconButton onClick={() => props.onDelete(key.keyId)} size="large">
								<DeleteIcon />
							</IconButton>
						</TableCell>
					</TableRow>
				))}
			</TableBody>
		</Table>
	);
};

export default ApiKeysTable;
